import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Buildings, 
  Globe, 
  CurrencyDollar, 
  Receipt, 
  Clock,
  CircleNotch,
  FloppyDisk,
  Phone,
  EnvelopeSimple,
  MapPin,
  LinkSimple,
  UsersThree,
  UploadSimple,
  Trash,
  Image,
  Stack,
  Crown,
  ShieldCheck,
  Warning,
  CheckCircle,
  Lock,
  Sparkle,
  TrendUp,
  ChartLineUp
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import DocumentThemeCard from '../components/company/DocumentThemeCard';
import CurrencyNormalizationCard from '../components/company/CurrencyNormalizationCard';
import AttachmentsMigrationCard from '../components/company/AttachmentsMigrationCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyPage() {
  const { user, hasPermission } = useAuth();
  const { refreshCompany } = useCompany();
  const [company, setCompany] = useState(null);
  const [logoHealth, setLogoHealth] = useState(null);
  const [options, setOptions] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Tax Validation Settings
  const [taxValidationSettings, setTaxValidationSettings] = useState({
    enable_vat_validation: false,
    enable_corporate_tax_validation: false,
    subscription_plan: 'basic',
    vat_filing_deadline_day: 28,
    corporate_tax_filing_month: 9,
    plan_features: {}
  });
  const [savingTaxSettings, setSavingTaxSettings] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    country: '',
    currency: 'AED',
    tax_enabled: false,
    tax_rate: '',
    tax_label: 'Tax',
    fiscal_year_start: '',
    date_format: 'YYYY-MM-DD',
    timezone: 'UTC',
    address: '',
    phone: '',
    email: '',
    website: '',
    multi_company_enabled: false,
    // Indian GST fields
    tax_regime: 'vat',
    gstin: '',
    pan: '',
    cin: '',
    tan: '',
    state_code: '',
    state_name: ''
  });
  
  // Indian State Codes
  const [indianStates, setIndianStates] = useState([]);

  const canEdit = hasPermission('settings', 'edit');
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companyRes, optionsRes, statsRes, taxSettingsRes, plansRes, statesRes, logoHealthRes] = await Promise.all([
        axios.get(`${API_URL}/company`),
        axios.get(`${API_URL}/company/options`),
        axios.get(`${API_URL}/company/stats`),
        axios.get(`${API_URL}/tax-validation/settings`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/tax-validation/subscription-plans`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gst/state-codes`).catch(() => ({ data: { states: [] } })),
        axios.get(`${API_URL}/company/logo/health`).catch(() => ({ data: null }))
      ]);
      
      setCompany(companyRes.data);
      setOptions(optionsRes.data);
      setStats(statsRes.data);
      setIndianStates(statesRes.data?.states || []);
      setLogoHealth(logoHealthRes.data);
      // If the health endpoint repaired the URL, sync the in-memory company doc
      if (logoHealthRes.data?.auto_repaired && logoHealthRes.data?.logo_url) {
        setCompany(prev => prev ? { ...prev, logo_url: logoHealthRes.data.logo_url } : prev);
      }
      
      // Set tax validation settings
      if (taxSettingsRes.data) {
        setTaxValidationSettings(taxSettingsRes.data);
      }
      setSubscriptionPlans(plansRes.data || []);
      
      // Populate form with existing data
      const c = companyRes.data;
      setFormData({
        name: c.name || '',
        industry: c.industry || '',
        country: c.country || '',
        currency: c.currency || 'AED',
        tax_enabled: c.tax_enabled || false,
        tax_rate: c.tax_rate?.toString() || '',
        tax_label: c.tax_label || 'Tax',
        fiscal_year_start: c.fiscal_year_start || '',
        date_format: c.date_format || 'YYYY-MM-DD',
        timezone: c.timezone || 'UTC',
        address: c.address || '',
        phone: c.phone || '',
        email: c.email || '',
        website: c.website || '',
        multi_company_enabled: c.multi_company_enabled || false,
        // Indian GST fields
        tax_regime: c.tax_regime || 'vat',
        gstin: c.gstin || '',
        pan: c.pan || '',
        cin: c.cin || '',
        tan: c.tan || '',
        state_code: c.state_code || '',
        state_name: c.state_name || ''
      });
    } catch (error) {
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null
      };
      
      const response = await axios.patch(`${API_URL}/company`, payload);
      setCompany(response.data);
      toast.success('Company settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, WebP, or SVG.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/company/logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompany(prev => ({ ...prev, logo_url: response.data.logo_url }));
      refreshCompany(); // Update global context
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the company logo?')) return;

    setUploadingLogo(true);
    try {
      await axios.delete(`${API_URL}/company/logo`);
      setCompany(prev => ({ ...prev, logo_url: null }));
      refreshCompany(); // Update global context
      toast.success('Logo deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleTaxValidationChange = (field, value) => {
    setTaxValidationSettings(prev => ({ ...prev, [field]: value }));
  };

  const saveTaxValidationSettings = async () => {
    setSavingTaxSettings(true);
    try {
      const response = await axios.patch(`${API_URL}/tax-validation/settings`, {
        enable_vat_validation: taxValidationSettings.enable_vat_validation,
        enable_corporate_tax_validation: taxValidationSettings.enable_corporate_tax_validation,
        vat_filing_deadline_day: taxValidationSettings.vat_filing_deadline_day,
        corporate_tax_filing_month: taxValidationSettings.corporate_tax_filing_month,
        ...(isSuperAdmin && { subscription_plan: taxValidationSettings.subscription_plan })
      });
      setTaxValidationSettings(response.data);
      toast.success('Tax validation settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tax settings');
    } finally {
      setSavingTaxSettings(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch(plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'pro': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl" data-testid="company-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Company Settings
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage your organization profile and preferences
          </p>
        </div>
        {canEdit && (
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-company-btn">
            {saving ? (
              <CircleNotch className="animate-spin mr-2" size={18} />
            ) : (
              <FloppyDisk className="mr-2" size={18} />
            )}
            Save Changes
          </Button>
        )}
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        <div className="card-tactile rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-500/10">
              <UsersThree size={22} className="text-blue-600 dark:text-blue-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Active Users</p>
              <p className="text-2xl font-heading font-semibold">{stats?.active_users || 0}</p>
            </div>
          </div>
        </div>
        <div className="card-tactile rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-500/10">
              <Globe size={22} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Region</p>
              <p className="text-2xl font-heading font-semibold">{formData.country || 'Not set'}</p>
            </div>
          </div>
        </div>
        <div className="card-tactile rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/10">
              <CurrencyDollar size={22} className="text-amber-600 dark:text-amber-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Currency</p>
              <p className="text-2xl font-heading font-semibold">{formData.currency}</p>
            </div>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-violet-500/10">
              <Image size={20} className="text-violet-600 dark:text-violet-400" weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium">Company Logo</h2>
              <p className="text-sm text-muted-foreground">Your logo will appear on invoices, quotations, and other documents</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              <div 
                className="w-40 h-40 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden"
                data-testid="logo-preview"
              >
                {company?.logo_url ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/company/${company.id}/logo?v=${encodeURIComponent(company.logo_updated_at || company.updated_at || '')}`} 
                    alt="Company Logo" 
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Buildings size={48} weight="thin" className="mx-auto mb-2 opacity-50" />
                    <p className="text-xs">No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-1 space-y-4">
              <div>
                <p className="text-sm font-medium mb-1">Upload Guidelines</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Supported formats: JPEG, PNG, GIF, WebP, SVG</li>
                  <li>• Maximum file size: 5MB</li>
                  <li>• Recommended: Square image (e.g., 512x512px)</li>
                  <li>• Transparent background works best</li>
                </ul>
              </div>

              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <CircleNotch className="animate-spin mr-2" size={16} />
                    ) : (
                      <UploadSimple className="mr-2" size={16} />
                    )}
                    {company?.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  {company?.logo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLogoDelete}
                      disabled={uploadingLogo}
                      className="text-destructive hover:text-destructive"
                      data-testid="delete-logo-btn"
                    >
                      <Trash className="mr-2" size={16} />
                      Remove
                    </Button>
                  )}
                </div>
              )}

              {/* Logo health alerts */}
              {logoHealth?.auto_repaired && (
                <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm flex items-start gap-2" data-testid="logo-auto-repaired-banner">
                  <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Logo restored.</strong> We detected a stale logo reference and automatically reconnected it to the latest uploaded file. Your documents will display the correct logo.
                  </div>
                </div>
              )}
              {logoHealth?.needs_reupload && (
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm flex items-start gap-2" data-testid="logo-missing-banner">
                  <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Logo file is missing.</strong> Please re-upload your logo so it appears on quotations, invoices and other PDFs.
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-primary/10">
              <Buildings size={20} className="text-primary" weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium">Company Profile</h2>
              <p className="text-sm text-muted-foreground">Basic organization information</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Company Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                disabled={!canEdit}
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Industry
              </Label>
              <Select 
                value={formData.industry} 
                onValueChange={(v) => handleChange('industry', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="company-industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {options?.industries?.map(ind => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Address
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-muted-foreground" size={16} />
                <Input
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="pl-10"
                  placeholder="123 Business St, City, Country"
                  disabled={!canEdit}
                  data-testid="company-address"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Phone
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="pl-10"
                  placeholder="+1 (555) 123-4567"
                  disabled={!canEdit}
                  data-testid="company-phone"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Email
              </Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="pl-10"
                  placeholder="contact@company.com"
                  disabled={!canEdit}
                  data-testid="company-email"
                />
              </div>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Website
              </Label>
              <div className="relative">
                <LinkSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="pl-10"
                  placeholder="https://www.company.com"
                  disabled={!canEdit}
                  data-testid="company-website"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Regional Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Globe size={20} className="text-blue-600 dark:text-blue-400" weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium">Regional Settings</h2>
              <p className="text-sm text-muted-foreground">Localization and regional preferences</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Country
              </Label>
              <Select 
                value={formData.country} 
                onValueChange={(v) => handleChange('country', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="company-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {options?.countries?.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Currency
              </Label>
              <Select 
                value={formData.currency} 
                onValueChange={(v) => handleChange('currency', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="company-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options?.currencies?.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Timezone
              </Label>
              <Select 
                value={formData.timezone} 
                onValueChange={(v) => handleChange('timezone', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="company-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options?.timezones?.map(tz => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Date Format
              </Label>
              <Select 
                value={formData.date_format} 
                onValueChange={(v) => handleChange('date_format', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="company-dateformat">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options?.date_formats?.map(df => (
                    <SelectItem key={df} value={df}>{df}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Tax Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Receipt size={20} className="text-amber-600 dark:text-amber-400" weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium">Tax Settings</h2>
              <p className="text-sm text-muted-foreground">Configure tax calculations for invoices</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable Tax</p>
                <p className="text-sm text-muted-foreground">Apply tax to invoices and transactions</p>
              </div>
              <Switch
                checked={formData.tax_enabled}
                onCheckedChange={(v) => handleChange('tax_enabled', v)}
                disabled={!canEdit}
                data-testid="company-tax-enabled"
              />
            </div>

            {formData.tax_enabled && (
              <>
                <Separator />
                
                {/* Tax Regime Selection */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                    Tax Regime
                  </Label>
                  <Select
                    value={formData.tax_regime}
                    onValueChange={(v) => {
                      handleChange('tax_regime', v);
                      // Auto-set tax label based on regime
                      if (v === 'gst') {
                        handleChange('tax_label', 'GST');
                        handleChange('tax_rate', '18');
                      } else if (v === 'vat') {
                        handleChange('tax_label', 'VAT');
                        handleChange('tax_rate', '5');
                      }
                    }}
                    disabled={!canEdit}
                  >
                    <SelectTrigger data-testid="tax-regime-select">
                      <SelectValue placeholder="Select tax regime" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vat">VAT (Value Added Tax)</SelectItem>
                      <SelectItem value="gst">GST (Goods & Services Tax - India)</SelectItem>
                      <SelectItem value="sales_tax">Sales Tax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                      Tax Label
                    </Label>
                    <Input
                      value={formData.tax_label}
                      onChange={(e) => handleChange('tax_label', e.target.value)}
                      placeholder="VAT, GST, Sales Tax, etc."
                      disabled={!canEdit}
                      data-testid="company-tax-label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                      Default Tax Rate (%)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.tax_rate}
                      onChange={(e) => handleChange('tax_rate', e.target.value)}
                      placeholder="18.00"
                      disabled={!canEdit}
                      data-testid="company-tax-rate"
                    />
                  </div>
                </div>
                
                {/* Indian GST Specific Fields */}
                {formData.tax_regime === 'gst' && (
                  <>
                    <Separator />
                    <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-lg">🇮🇳</span>
                        <h4 className="font-semibold text-orange-700 dark:text-orange-400">Indian GST Configuration</h4>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                            GSTIN (15 digits)
                          </Label>
                          <Input
                            value={formData.gstin}
                            onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                            placeholder="22AAAAA0000A1Z5"
                            maxLength={15}
                            disabled={!canEdit}
                            data-testid="company-gstin"
                          />
                          <p className="text-xs text-muted-foreground">Goods and Services Tax Identification Number</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                            PAN (10 characters)
                          </Label>
                          <Input
                            value={formData.pan}
                            onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                            placeholder="AAAAA0000A"
                            maxLength={10}
                            disabled={!canEdit}
                            data-testid="company-pan"
                          />
                          <p className="text-xs text-muted-foreground">Permanent Account Number</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                            State
                          </Label>
                          <Select
                            value={formData.state_code}
                            onValueChange={(v) => {
                              handleChange('state_code', v);
                              const state = indianStates.find(s => s.code === v);
                              handleChange('state_name', state?.name || '');
                            }}
                            disabled={!canEdit}
                          >
                            <SelectTrigger data-testid="company-state">
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {indianStates.map(state => (
                                <SelectItem key={state.code} value={state.code}>
                                  {state.code} - {state.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                            CIN (Optional)
                          </Label>
                          <Input
                            value={formData.cin}
                            onChange={(e) => handleChange('cin', e.target.value.toUpperCase())}
                            placeholder="L12345MH2000PLC123456"
                            disabled={!canEdit}
                            data-testid="company-cin"
                          />
                          <p className="text-xs text-muted-foreground">Corporate Identity Number</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                            TAN (Optional)
                          </Label>
                          <Input
                            value={formData.tan}
                            onChange={(e) => handleChange('tan', e.target.value.toUpperCase())}
                            placeholder="AAAA00000A"
                            maxLength={10}
                            disabled={!canEdit}
                            data-testid="company-tan"
                          />
                          <p className="text-xs text-muted-foreground">Tax Deduction Account Number</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-400">
                          <strong>GST Rate Slabs:</strong> 0%, 5%, 12%, 18%, 28% - Configure per item/service
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Fiscal Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Clock size={20} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium">Fiscal Year</h2>
              <p className="text-sm text-muted-foreground">Set your financial year start date</p>
            </div>
          </div>

          <div className="space-y-2 max-w-xs">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Fiscal Year Start (MM-DD)
            </Label>
            <Input
              value={formData.fiscal_year_start}
              onChange={(e) => handleChange('fiscal_year_start', e.target.value)}
              placeholder="01-01"
              pattern="\d{2}-\d{2}"
              disabled={!canEdit}
              data-testid="company-fiscal-year"
            />
            <p className="text-xs text-muted-foreground">
              Example: 01-01 for January 1st, 04-01 for April 1st
            </p>
          </div>
        </motion.div>

        {/* Multi-Company Settings - Super Admin Only */}
        {isSuperAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="card-tactile rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Stack size={20} className="text-purple-600 dark:text-purple-400" weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-lg font-medium">Multi-Company Management</h2>
                  <Badge variant="outline" className="text-xs">
                    <Crown size={12} className="mr-1" /> Super Admin
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">Allow users to create and manage multiple companies</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                <div>
                  <p className="font-medium">Enable Multi-Company</p>
                  <p className="text-sm text-muted-foreground">
                    When enabled, users can create additional companies and switch between them.
                    This is typically a premium feature tied to subscription plans.
                  </p>
                </div>
                <Switch
                  checked={formData.multi_company_enabled}
                  onCheckedChange={(v) => handleChange('multi_company_enabled', v)}
                  disabled={!canEdit}
                  data-testid="company-multi-company-toggle"
                />
              </div>
              
              {formData.multi_company_enabled && (
                <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <div className="flex items-start gap-3">
                    <Stack size={20} className="text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-purple-600 dark:text-purple-400">Multi-Company Mode Active</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Users with appropriate permissions can now:
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Create new companies from the sidebar dropdown</li>
                        <li>• Switch between companies they have access to</li>
                        <li>• Invite users to their companies</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Tax Validation Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card-tactile rounded-lg p-6"
          data-testid="tax-validation-settings"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-medium">Tax Validation Settings</h2>
                <p className="text-sm text-muted-foreground">Configure pre-filing validation for UAE compliance</p>
              </div>
            </div>
            <Badge className={getPlanBadgeColor(taxValidationSettings.subscription_plan)}>
              {taxValidationSettings.subscription_plan?.toUpperCase()} Plan
            </Badge>
          </div>

          {/* Subscription Plan Info */}
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/30 border border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-3">
              <Sparkle size={20} className="text-amber-500 mt-0.5" weight="fill" />
              <div className="flex-1">
                <p className="font-medium text-sm">Plan Features</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  <div className="flex items-center gap-1.5 text-xs">
                    {taxValidationSettings.plan_features?.vat_validation ? 
                      <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : 
                      <Lock size={14} className="text-muted-foreground" />}
                    <span className={!taxValidationSettings.plan_features?.vat_validation ? 'text-muted-foreground' : ''}>VAT Validation</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    {taxValidationSettings.plan_features?.corporate_tax_validation ? 
                      <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : 
                      <Lock size={14} className="text-muted-foreground" />}
                    <span className={!taxValidationSettings.plan_features?.corporate_tax_validation ? 'text-muted-foreground' : ''}>Corporate Tax Validation</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    {taxValidationSettings.plan_features?.trend_analysis ? 
                      <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : 
                      <Lock size={14} className="text-muted-foreground" />}
                    <span className={!taxValidationSettings.plan_features?.trend_analysis ? 'text-muted-foreground' : ''}>Trend Analysis</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    {taxValidationSettings.plan_features?.risk_scoring ? 
                      <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : 
                      <Lock size={14} className="text-muted-foreground" />}
                    <span className={!taxValidationSettings.plan_features?.risk_scoring ? 'text-muted-foreground' : ''}>Risk Scoring</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    {taxValidationSettings.plan_features?.advanced_analytics ? 
                      <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : 
                      <Lock size={14} className="text-muted-foreground" />}
                    <span className={!taxValidationSettings.plan_features?.advanced_analytics ? 'text-muted-foreground' : ''}>Advanced Analytics</span>
                  </div>
                </div>
                {taxValidationSettings.subscription_plan === 'basic' && (
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                    <TrendUp size={12} />
                    Upgrade to Pro or Enterprise to unlock validation features
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Super Admin: Plan Selection */}
          {isSuperAdmin && (
            <div className="mb-6 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Crown size={16} className="text-purple-600" />
                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Super Admin Controls</span>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                  Subscription Plan
                </Label>
                <Select 
                  value={taxValidationSettings.subscription_plan} 
                  onValueChange={(v) => handleTaxValidationChange('subscription_plan', v)}
                >
                  <SelectTrigger className="w-full sm:w-64" data-testid="subscription-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">
                      <div className="flex items-center gap-2">
                        <span>Basic</span>
                        <span className="text-xs text-muted-foreground">- No validation</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pro">
                      <div className="flex items-center gap-2">
                        <span>Pro</span>
                        <span className="text-xs text-muted-foreground">- VAT validation</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="enterprise">
                      <div className="flex items-center gap-2">
                        <span>Enterprise</span>
                        <span className="text-xs text-muted-foreground">- Full validation + analytics</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <Separator className="my-6" />

          {/* Validation Toggles */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <ShieldCheck size={16} />
              Validation Controls
            </h3>
            
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">Enable VAT Validation</p>
                  {!taxValidationSettings.plan_features?.vat_validation && (
                    <Badge variant="outline" className="text-xs">Pro+</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Run validation checks before filing VAT returns. Includes data completeness, calculation integrity, and anomaly detection.
                </p>
              </div>
              <Switch
                checked={taxValidationSettings.enable_vat_validation}
                onCheckedChange={(v) => handleTaxValidationChange('enable_vat_validation', v)}
                disabled={!canEdit || !taxValidationSettings.plan_features?.vat_validation}
                data-testid="enable-vat-validation"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">Enable Corporate Tax Validation</p>
                  {!taxValidationSettings.plan_features?.corporate_tax_validation && (
                    <Badge variant="outline" className="text-xs">Enterprise</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Run validation checks before filing Corporate Tax. Includes P&L validation, adjustment checks, and YoY comparison.
                </p>
              </div>
              <Switch
                checked={taxValidationSettings.enable_corporate_tax_validation}
                onCheckedChange={(v) => handleTaxValidationChange('enable_corporate_tax_validation', v)}
                disabled={!canEdit || !taxValidationSettings.plan_features?.corporate_tax_validation}
                data-testid="enable-ct-validation"
              />
            </div>
          </div>

          <Separator className="my-6" />

          {/* Filing Deadlines */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Clock size={16} />
              Filing Deadlines
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                  VAT Filing Deadline (Day of Month)
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={taxValidationSettings.vat_filing_deadline_day}
                  onChange={(e) => handleTaxValidationChange('vat_filing_deadline_day', parseInt(e.target.value) || 28)}
                  disabled={!canEdit}
                  data-testid="vat-deadline-day"
                />
                <p className="text-xs text-muted-foreground">UAE VAT returns due by 28th of month following the period</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                  Corporate Tax Filing Month
                </Label>
                <Select 
                  value={String(taxValidationSettings.corporate_tax_filing_month)}
                  onValueChange={(v) => handleTaxValidationChange('corporate_tax_filing_month', parseInt(v))}
                  disabled={!canEdit}
                >
                  <SelectTrigger data-testid="ct-filing-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={String(m)}>
                        {new Date(2000, m-1).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Month when corporate tax returns are typically due</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 pt-4 border-t border-border flex justify-end">
            <Button 
              onClick={saveTaxValidationSettings} 
              disabled={savingTaxSettings || !canEdit}
              data-testid="save-tax-settings-btn"
            >
              {savingTaxSettings ? (
                <CircleNotch className="animate-spin mr-2" size={16} />
              ) : (
                <FloppyDisk className="mr-2" size={16} />
              )}
              Save Tax Settings
            </Button>
          </div>
        </motion.div>

        {/* Company ID Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="p-4 rounded-lg bg-muted/50 border border-border"
        >
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Company ID:</span> {company?.id}
            <span className="mx-3">•</span>
            <span className="font-medium">Created:</span> {new Date(company?.created_at).toLocaleDateString()}
            <span className="mx-3">•</span>
            <span className="font-medium">Users:</span> {company?.user_count}
          </p>
        </motion.div>
      </form>

      {/* Document Theme — applied to generated PDFs */}
      <DocumentThemeCard
        company={company}
        canEdit={canEdit}
        onSaved={(updated) => { setCompany(updated); if (refreshCompany) refreshCompany(); }}
      />

      {/* Currency normalization — sweep legacy USD contacts to company currency */}
      <CurrencyNormalizationCard company={company} canEdit={canEdit} />

      {/* Attachment storage — back-fill MongoDB GridFS for redeploy survivability */}
      <AttachmentsMigrationCard canEdit={canEdit} />
    </div>
  );
}
